import { useCallback } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import emitUserInteractionEvent, {
  UserInteractionName,
} from "./emitUserInteractionEvent";
import { SERVICE_NAME } from "@zapier/utils";

export enum TemplateDuplicateModalAction {
  Discarded = "discarded",
  Duplicated = "duplicated",
  Failed = "failed",
  Viewed = "viewed",
}

/**
 * Why a failed duplication failed. Mirrors the editor's EditFailedErrorType
 * (configurableTemplateEvents.ts) so both halves of the guided-templates
 * funnel classify errors identically.
 */
export type TemplateDuplicateErrorType =
  | "not_found"
  | "permission_denied"
  | "network"
  | "server_error"
  | "unknown";

interface TemplateDuplicateModalFields {
  /** The source template being copied. */
  template_id?: string;
  customuser_id?: number;
  /**
   * The created copy, on `duplicated` only. This is the lineage the templates
   * table lacks — it has no parent/source column, so without this a duplicate
   * cannot be told apart from a newly created template (CRTN-10662).
   */
  new_template_id?: string;
  /** Whether the user renamed the copy or kept the default "Copy of ...". */
  name_changed?: boolean;
  error_type?: TemplateDuplicateErrorType;
}

/**
 * Guided template duplicate-modal interactions (CRTN-10662).
 *
 * Separate from useEmitTemplateKebabInteractionEvent so the entry point stays
 * distinguishable from what happens inside the modal: the kebab click emits
 * `template_kebab_interaction` with option_selected: 'duplicate', and
 * everything after it lands here. Before this existed, the modal's success
 * reused the kebab event, so one name meant both "opened" and "completed".
 *
 * Follows the InterfaceDuplicateModal precedent in UserInteractionName rather
 * than the editor's `configurable_template.*` naming — this repo's interaction
 * names come from that closed enum. The two funnels join on
 * template_id + account_id.
 */
export function useEmitTemplateDuplicateModalEvent() {
  const accountId = useCurrentAccountId();

  const emit = useCallback(
    (
      optionSelected: TemplateDuplicateModalAction,
      customTrackingFields?: TemplateDuplicateModalFields,
    ) =>
      // Returned rather than discarded so call sites can attach a .catch and
      // keep a rejected emit from becoming an unhandled rejection.
      emitUserInteractionEvent({
        accountId,
        eventAction: "click",
        interactionName: UserInteractionName.TemplateDuplicateModal,
        optionSelected,
        systemName: SERVICE_NAME,
        customTrackingFields: {
          ...customTrackingFields,
          ...(customTrackingFields?.name_changed !== undefined && {
            name_changed: String(customTrackingFields.name_changed),
          }),
          // Kept in step with useEmitTemplateKebabInteractionEvent, so the
          // duplicate funnel carries the same attribution as the kebab click
          // that opened it.
          referring_source: "guided_templates_assets",
        },
      }),
    [accountId],
  );

  return {
    emit,
  };
}
